import React, { useState, useRef, useMemo } from 'react';

interface BreakEvenChartProps {
  data: {
    year: number;
    strategyBuyPoints: number; // Value of invested monthly savings
    strategyInvest: number; // Value of invested upfront cash (formerly strategyKeepCash)
  }[];
  refinanceYear?: number | null;
}

export const BreakEvenChart: React.FC<BreakEvenChartProps> = ({ data, refinanceYear }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; year: number; buyPoints: number; invest: number } | null>(null);

  const viewBox = { width: 600, height: 300 };
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };

  const { paths, yAxisLabels, xScale, yScale, refinanceX } = useMemo(() => {
    const xMax = data.length > 0 ? data[data.length - 1].year : 30;
    
    // Calculate max value with fallback to prevent -Infinity
    const rawMax = data.length > 0 
        ? Math.max(...data.map(d => Math.max(d.strategyBuyPoints, d.strategyInvest))) 
        : 10000;
        
    const yMax = rawMax * 1.1; // 10% buffer

    const effectiveWidth = viewBox.width - padding.left - padding.right;
    const effectiveHeight = viewBox.height - padding.top - padding.bottom;

    const xScale = (year: number) => padding.left + (year / xMax) * effectiveWidth;
    const yScale = (val: number) => padding.top + effectiveHeight - (val / yMax) * effectiveHeight;

    // Create lines
    const createPath = (key: 'strategyBuyPoints' | 'strategyInvest') => {
        return data.map((d, i) => 
            `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d[key])}`
        ).join(' ');
    };

    const paths = {
        buyPoints: createPath('strategyBuyPoints'),
        invest: createPath('strategyInvest')
    };

    // Y Axis Labels (0, 50%, 100%)
    const yAxisLabels = [0, yMax / 2, yMax].map(val => ({
        value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(val),
        y: yScale(val)
    }));
    
    const refinanceX = refinanceYear ? xScale(refinanceYear) : null;

    return { paths, yAxisLabels, xScale, yScale, refinanceX };
  }, [data, refinanceYear]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    
    // Fix: Safely handle null CTM to prevent "Uncaught TypeError"
    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const { x } = pt.matrixTransform(ctm.inverse());

    // Find closest data point
    const effectiveWidth = viewBox.width - padding.left - padding.right;
    const ratio = Math.max(0, Math.min(1, (x - padding.left) / effectiveWidth));
    const yearIndex = Math.round(ratio * (data.length - 1));
    
    if (yearIndex >= 0 && yearIndex < data.length) {
        const d = data[yearIndex];
        setTooltip({
            x: xScale(d.year),
            y: yScale(Math.max(d.strategyBuyPoints, d.strategyInvest)), // anchor near top line
            year: d.year,
            buyPoints: d.strategyBuyPoints,
            invest: d.strategyInvest
        });
    }
  };

  return (
    <div className="w-full h-full relative select-none">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
        className="w-full h-full overflow-visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* Grid */}
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={viewBox.height - padding.bottom} strokeWidth="1" className="stroke-gray-200 dark:stroke-gray-600" />
        <line x1={padding.left} y1={viewBox.height - padding.bottom} x2={viewBox.width - padding.right} y2={viewBox.height - padding.bottom} strokeWidth="1" className="stroke-gray-200 dark:stroke-gray-600" />

        {/* Y-Axis Labels */}
        {yAxisLabels.map((lbl, i) => (
            <text key={i} x={padding.left - 10} y={lbl.y + 4} textAnchor="end" fontSize="10" className="fill-gray-500 dark:fill-gray-400">{lbl.value}</text>
        ))}

        {/* X-Axis Labels (0, 10, 20, 30) */}
        {[0, 10, 20, 30].map(yr => (
            <text key={yr} x={xScale(yr)} y={viewBox.height - 15} textAnchor="middle" fontSize="10" className="fill-gray-500 dark:fill-gray-400">Yr {yr}</text>
        ))}
        
        {/* Refinance Line */}
        {refinanceX && (
             <g>
                <line 
                    x1={refinanceX} 
                    y1={padding.top} 
                    x2={refinanceX} 
                    y2={viewBox.height - padding.bottom} 
                    stroke="#ef4444" 
                    strokeWidth="2" 
                    strokeDasharray="5,5" 
                />
                <text x={refinanceX} y={padding.top - 5} textAnchor="middle" fontSize="10" fill="#ef4444" fontWeight="bold">Refinance</text>
            </g>
        )}

        {/* Strategy B: Invest (Blue) */}
        <path d={paths.invest} fill="none" stroke="#3b82f6" strokeWidth="3" />
        
        {/* Strategy A: Buy Points (Green) */}
        <path d={paths.buyPoints} fill="none" stroke="#22c55e" strokeWidth="3" />

        {/* Tooltip Line & Dot */}
        {tooltip && (
             <line x1={tooltip.x} y1={padding.top} x2={tooltip.x} y2={viewBox.height - padding.bottom} stroke="#94a3b8" strokeDasharray="4" />
        )}

      </svg>

      {/* Custom HTML Legend/Tooltip Overlay */}
      {tooltip && (
          <div 
            className="absolute z-10 bg-gray-900 text-white text-xs p-3 rounded shadow-xl pointer-events-none"
            style={{
                top: '10%', 
                left: '50%',
                transform: 'translateX(-50%)'
            }}
          >
              <div className="font-bold mb-1 text-center">Year {tooltip.year}</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="text-green-400 font-semibold">Buy Points Strategy:</div>
                  <div className="text-right">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tooltip.buyPoints)}</div>
                  
                  <div className="text-blue-400 font-semibold">Invest Strategy:</div>
                  <div className="text-right">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tooltip.invest)}</div>
                  
                  <div className="col-span-2 border-t border-gray-700 mt-1 pt-1 text-center">
                      Winner: <span className="font-bold text-white">
                          {tooltip.buyPoints > tooltip.invest ? 'Buying Points' : 'Investing'}
                      </span>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};